import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowRight, Check, ChevronDown } from 'lucide-react';
import { Card } from './SubscriptionAppCard';
import { DEFAULT_SUBSCRIPTION_ACTIVITY_VIEW, formatRelativeHours, subscriptionActivityEvents, subscriptionActivityViews, subscriptionEventTypeBadge } from '../data/mockSubscriptionActivity';
const MAX_ITEMS = 10;
const WINDOW_HOURS = 24 * 7;
export function SubscriptionActivityCard() {
  const navigate = useNavigate();
  const [activeViewId, setActiveViewId] = useState(DEFAULT_SUBSCRIPTION_ACTIVITY_VIEW);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const activeView = subscriptionActivityViews.find(view => view.id === activeViewId) ?? subscriptionActivityViews[subscriptionActivityViews.length - 1];
  const visibleEvents = subscriptionActivityEvents.filter(event => event.hoursAgo <= WINDOW_HOURS && (activeView.events.length === 0 || activeView.events.includes(event.kind))).slice(0, MAX_ITEMS);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);
  const openActivityLog = (eventId?: string) => {
    navigate('/operations/activity-log', {
      state: {
        subscriptionActivityView: {
          id: activeView.id,
          name: activeView.name,
          events: activeView.events
        },
        ...(eventId ? {
          eventId
        } : {})
      }
    });
  };
  return <Card padding="none" data-id="element-5513" data-mp="3kxcs2dv9by22">
      <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center gap-3 bg-white rounded-t-lg" data-id="element-5514" data-mp="3kwkvazpxbua7">
        <div className="flex items-center gap-2 min-w-0" data-id="element-5515" data-mp="3fx86i9fhsd4z">
          <Activity className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" data-id="element-5516" data-mp="3un5zd2od9r0i" />
          <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-5517" data-mp="3up8q5oxcfo6a">
            Subscription Activity
          </h3>
        </div>

        <div className="relative flex-shrink-0" ref={menuRef} data-id="element-5518" data-mp="31yguqto3r3k0">
          <button type="button" onClick={() => setMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={menuOpen} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-[#CBD5E1] bg-white text-xs font-medium text-[#475569] hover:bg-[#F8FAFC] transition-colors max-w-[180px]" data-id="element-5519" data-mp="31yqo8r8cvsug">
            <span className="truncate" data-id="element-5520" data-mp="36zo5n1ljkiuw">{activeView.name}</span>
            <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5521" data-mp="q7wko4pk07bx" />
          </button>

          {menuOpen && <div role="menu" className="absolute right-0 top-full mt-1 w-60 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-20 py-1" data-id="element-5522" data-mp="m6az7uyyekq4">
              <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5523" data-mp="tsy7unktucie">
                Views
              </div>
              {subscriptionActivityViews.map(view => <button key={view.id} type="button" role="menuitemradio" aria-checked={view.id === activeView.id} onClick={() => {
            setActiveViewId(view.id);
            setMenuOpen(false);
          }} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[#0F172A] hover:bg-[#F8FAFC]" data-id="element-5524" data-mp="jusc6npm62lz">
                  <span data-id="element-5525" data-mp="12s38bzfy8fx7">{view.name}</span>
                  {view.id === activeView.id && <Check className="w-4 h-4 text-[#0EA5E9] flex-shrink-0" data-id="element-5526" data-mp="12ppd9ql9r3pn" />}
                </button>)}
              <div className="my-1 h-px bg-[#E2E8F0]" data-id="element-5527" data-mp="17oa7n9duat66" />
              <button type="button" role="menuitem" onClick={() => setMenuOpen(false)} className="w-full px-3 py-2 text-left text-sm text-[#475569] hover:bg-[#F8FAFC]" data-id="element-5528" data-mp="1vffkrgseb7cs">
                Save current view…
              </button>
              <button type="button" role="menuitem" onClick={() => setMenuOpen(false)} className="w-full px-3 py-2 text-left text-sm text-[#475569] hover:bg-[#F8FAFC]" data-id="element-5529" data-mp="cgj8ukulptxs">
                Manage views
              </button>
            </div>}
        </div>
      </div>

      <div className="bg-white rounded-b-lg" data-id="element-5530" data-mp="3trsre3zucqvi">
        {visibleEvents.length === 0 ? <p className="px-6 py-8 text-sm text-[#94A3B8] text-center m-0" data-id="element-5531" data-mp="v9n58dz7ekw3">
            No matching activity in the last 7 days
          </p> : <ul className="py-2" data-id="element-5532" data-mp="qbc02gjusyk7">
            {visibleEvents.map(event => {
          const badge = subscriptionEventTypeBadge[event.eventType];
          return <li key={event.id} data-id="element-5533" data-mp="gk7lhhy3bfpw">
                  <button type="button" onClick={() => openActivityLog(event.id)} className="w-full text-left px-6 py-2.5 hover:bg-[#F8FAFC] transition-colors flex items-center gap-4" data-id="element-5534" data-mp="gj68v91fel62">
                    <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-5535" data-mp="198mu9mqmp1ss">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0" style={{
                  backgroundColor: badge.bg,
                  color: badge.text
                }} data-id="element-5536" data-mp="1hhsl3zu4bojn">
                        {event.eventType}
                      </span>
                      <span className="text-sm text-[#0F172A] truncate" data-id="element-5537" data-mp="1vhk46ezhrv4i">
                        {event.description}
                      </span>
                    </div>
                    <span className="text-xs text-[#64748B] whitespace-nowrap flex-shrink-0" data-id="element-5538" data-mp="24hqgvynd8r2m">
                      {event.memberName} · {formatRelativeHours(event.hoursAgo)}
                    </span>
                  </button>
                </li>;
        })}
          </ul>}

        <div className="px-6 pb-5 pt-1" data-id="element-5539" data-mp="2e9w6j1q5awen">
          <button type="button" onClick={() => openActivityLog()} className="w-full text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] flex items-center justify-center gap-1 transition-colors" data-id="element-5540" data-mp="2j7ehsyrmxnpt">
            View all in Activity Log <ArrowRight className="w-4 h-4" data-id="element-5541" data-mp="1yilr2rjxhoh9" />
          </button>
        </div>
      </div>
    </Card>;
}